import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, Loader2, Sparkles, CheckCircle2, AlertTriangle, XCircle, X, ArrowRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api, { industrialProductsAPI, supplierProductsAPI } from '@/lib/api';
import { PhoneCaptureButton } from '@/components/PhoneCapture';

import { BASE_ORDER_UNITS, BASE_BILLING_UNITS, loadUnits, canonicalizeUnit } from '@/lib/units';

const getOrderUnits = () => loadUnits('customOrderUnits', 'deletedOrderUnits', BASE_ORDER_UNITS);
const getBillingUnits = () => loadUnits('customBillingUnits', 'deletedBillingUnits', BASE_BILLING_UNITS);
const withCurrent = (list, current) => (current && !list.includes(current) ? [current, ...list] : list);

/* Menu déroulant rendu DANS le dialog (pas de popup natif ni de portal) :
   le verrou de scroll du Dialog Radix bloque le défilement des popups natifs
   sur Android Chrome — une liste en flux dans le dialog défile toujours. */
const NativeSelect = ({ label, value, onChange, options, testId }) => {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    const norm = options.map(o => (typeof o === 'object' && o !== null ? o : { value: o, label: o }));
    const current = norm.find(o => String(o.value ?? '') === String(value ?? ''));
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('pointerdown', h);
        return () => document.removeEventListener('pointerdown', h);
    }, [open]);
    return (
        <div className="space-y-1 min-w-0" ref={boxRef}>
            {label && <Label className="text-xs">{label}</Label>}
            <button type="button" onClick={() => setOpen(v => !v)} data-testid={testId}
                className="w-full h-8 text-sm border border-input rounded-md px-2 bg-background shadow-sm text-left flex items-center justify-between gap-1 focus:outline-none focus:ring-1 focus:ring-ring">
                <span className="truncate">{current?.label ?? '—'}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="max-h-44 overflow-y-auto overscroll-contain border border-orange-200 rounded-md bg-white shadow-md divide-y divide-slate-50"
                    data-testid={testId ? `${testId}-list` : undefined}>
                    {norm.map(o => (
                        <button key={String(o.value)} type="button"
                            onClick={() => { onChange({ target: { value: o.value } }); setOpen(false); }}
                            className={`w-full text-left px-2.5 py-2 text-sm ${String(o.value ?? '') === String(value ?? '') ? 'bg-orange-50 text-orange-700 font-semibold' : 'hover:bg-slate-50'}`}>
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const ColisageBlock = ({ spForm, setSpForm }) => {
    const orderUnits = withCurrent(getOrderUnits(), spForm.order_unit);
    const billingUnits = withCurrent(getBillingUnits(), spForm.billing_unit);
    return (
        <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Colisage (1 unité de commande = X unités de facturation)</Label>
            <div className="flex items-start gap-2">
                <span className="text-xs text-slate-500 shrink-0 pt-2">1</span>
                <div className="flex-1 min-w-0">
                    <NativeSelect value={spForm.order_unit} testId="assistant-sp-order-unit"
                        onChange={(e) => setSpForm(f => ({ ...f, order_unit: e.target.value }))} options={orderUnits} />
                </div>
                <span className="text-xs text-slate-500 shrink-0 pt-2">de</span>
                <Input type="number" step="0.01" value={spForm.packaging_qty ?? ''} onChange={(e) => setSpForm(f => ({ ...f, packaging_qty: e.target.value }))}
                    placeholder="Qté" className="h-8 text-sm w-20 text-center shrink-0" data-testid="assistant-sp-packaging-qty" />
                <div className="flex-1 min-w-0">
                    <NativeSelect value={spForm.billing_unit} testId="assistant-sp-billing-unit"
                        onChange={(e) => setSpForm(f => ({ ...f, billing_unit: e.target.value }))} options={billingUnits} />
                </div>
            </div>
            {spForm.packaging_qty && (
                <p className="text-[11px] text-slate-400">Ex : commander 1 {spForm.order_unit} = {spForm.packaging_qty} {spForm.billing_unit}</p>
            )}
        </div>
    );
};

const CheckRow = ({ label, checked, onChange, testId }) => (
    <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer select-none">
        <input type="checkbox" checked={!!checked} onChange={onChange} className="w-4 h-4 accent-orange-500 shrink-0" data-testid={testId} />
        {label}
    </label>
);

const Field = ({ label, value, onChange, placeholder, type = 'text', testId }) => (
    <div className="space-y-1">
        <Label className="text-xs">{label}</Label>
        <Input type={type} value={value ?? ''} onChange={onChange} placeholder={placeholder} className="h-8 text-sm" data-testid={testId} />
    </div>
);

/** Assistant photo : contrôle d'existence + création guidée de produits
 *  industriels et fournisseurs. Chaque étape est validée par l'utilisateur. */
export default function ProductPhotoAssistantDialog({ open, onClose, suppliers = [], categories = [], onCreated, prefillLine = null, onProductCreated = null }) {
    const [step, setStep] = useState('setup'); // setup | analyzing | review | create | done
    const [supplierId, setSupplierId] = useState('');
    const [file, setFile] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [lines, setLines] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [queue, setQueue] = useState([]);
    const [qIdx, setQIdx] = useState(0);
    const [phase, setPhase] = useState('pi_confirm'); // pi_confirm | ip | sp (dans l'étape create)
    const [ips, setIps] = useState([]);
    const [familles, setFamilles] = useState([]);
    const [ipSearch, setIpSearch] = useState('');
    const [showIpPicker, setShowIpPicker] = useState(false);
    const [ipForm, setIpForm] = useState({});
    const [spForm, setSpForm] = useState({});
    const [currentIp, setCurrentIp] = useState(null); // PI existant ou créé pour la ligne courante
    const [saving, setSaving] = useState(false);
    const [created, setCreated] = useState([]);
    const cameraRef = useRef(null);

    const reset = () => {
        setStep('setup'); setSupplierId(''); setFile(null); setPhotos([]); setLines([]);
        setSelected(new Set()); setQueue([]); setQIdx(0); setCreated([]);
    };

    const analyze = async () => {
        if (!supplierId || (!file && photos.length === 0)) { toast.error('Choisissez un fournisseur et une photo/PDF'); return; }
        setStep('analyzing');
        try {
            const fd = new FormData();
            fd.append('supplier_id', supplierId);
            if (photos.length > 0) photos.forEach(p => fd.append('photos', p));
            else fd.append('pdf', file);
            const res = await api.post('/products/photo-assistant/analyze', fd, { timeout: 180000, headers: { 'Content-Type': 'multipart/form-data' } });
            const ls = res.data.lines || [];
            if (ls.length === 0) { toast.error('Aucun produit détecté sur le document'); setStep('setup'); return; }
            setLines(ls);
            setSelected(new Set(ls.map((l, i) => (!l.sp_match ? i : null)).filter(i => i !== null)));
            setStep('review');
        } catch (e) {
            const det = e.response?.data?.detail;
            toast.error(typeof det === 'string' ? det : 'Erreur d\'analyse');
            setStep('setup');
        }
    };

    const statusOf = (l) => l.sp_match ? 'ok' : (l.ip_match ? 'sp_missing' : 'ip_missing');

    const startCreation = () => {
        const q = Array.from(selected).sort((a, b) => a - b).map(i => ({ ...lines[i], _idx: i }));
        if (q.length === 0) { toast.error('Sélectionnez au moins un produit à créer'); return; }
        setQueue(q); setQIdx(0);
        prepareLine(q[0]);
        setStep('create');
    };

    React.useEffect(() => {
        if (open) {
            industrialProductsAPI.getAll().then(r => setIps(r.data || [])).catch(() => {});
            industrialProductsAPI.getFamilles().then(r => setFamilles(r.data || [])).catch(() => {});
            if (prefillLine) {
                // Mode « ligne unique » (réception facture IA) : pas d'analyse photo,
                // on passe directement à la création pré-remplie avec la ligne de facture.
                setSupplierId(prefillLine.supplier_id);
                const line = {
                    designation: prefillLine.designation || '',
                    ref: prefillLine.ref || '',
                    unit_price: prefillLine.unit_price ?? '',
                    unit: prefillLine.unit || '',
                    brand: '', packaging: '', capacity_value: '', capacity_unit: '',
                    packaging_qty: '', vat_rate: 8.5, ip_match: null, _idx: 0,
                };
                setQueue([line]); setQIdx(0);
                prepareLine(line);
                setStep('create');
            }
        }
    }, [open]);

    const prepareLine = (line) => {
        setIpSearch(''); setShowIpPicker(false);
        setCurrentIp(line.ip_match ? { id: line.ip_match.id, name: line.ip_match.name } : null);
        setPhase('pi_confirm');
    };

    const acceptIp = (ip) => {
        setCurrentIp({ id: ip.id, name: ip.name });
        setPhase('sp');
        prepareSpForm(queue[qIdx]);
    };

    const goCreateIp = () => {
        const line = queue[qIdx];
        setPhase('ip');
        setIpForm({
            name: line.designation, category: '', famille: '', unit: line.unit === 'kg' ? 'kg' : 'piece',
            vat_rate: line.vat_rate ?? 8.5, description: '',
            fuji_express: true, concept_lab: true, my_food: true, boucherie: true,
        });
        prepareSpForm(line);
    };

    const prepareSpForm = (line) => {
        setSpForm({
            supplier_ref: line.ref || '',
            supplier_name_product: line.designation,
            brand: line.brand || '',
            unit_price: line.unit_price ?? '',
            packaging: line.packaging || '',
            capacity_value: line.capacity_value ?? '',
            capacity_unit: line.capacity_unit || '',
            order_unit: getOrderUnits().includes(canonicalizeUnit(line.unit)) ? canonicalizeUnit(line.unit) : 'Pièce',
            billing_unit: getBillingUnits().includes(canonicalizeUnit(line.unit)) ? canonicalizeUnit(line.unit) : 'Pièce',
            packaging_qty: line.packaging_qty ?? '',
            supplier_id: supplierId,
            commandable: true,
            sell_by_detail: false,
            variable_weight: false,
            unknown_packaging: false,
            min_order_qty: 1,
        });
    };

    const createIp = async () => {
        if (!ipForm.name?.trim()) { toast.error('Nom du produit industriel requis'); return; }
        setSaving(true);
        try {
            const res = await industrialProductsAPI.create({
                name: ipForm.name.trim(),
                category: ipForm.category || '',
                famille: ipForm.famille || '',
                unit: ipForm.unit || 'piece',
                vat_rate: parseFloat(ipForm.vat_rate) || 8.5,
                description: ipForm.description || '',
                fuji_express: !!ipForm.fuji_express,
                concept_lab: !!ipForm.concept_lab,
                my_food: !!ipForm.my_food,
                boucherie: !!ipForm.boucherie,
            });
            setCurrentIp({ id: res.data.id, name: res.data.name });
            setCreated(prev => [...prev, { type: 'PI', name: res.data.name }]);
            toast.success(`Produit industriel « ${res.data.name} » créé`);
            setPhase('sp');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de création du produit industriel');
        } finally { setSaving(false); }
    };

    const createSp = async () => {
        if (!spForm.supplier_name_product?.trim()) { toast.error('Désignation requise'); return; }
        if (spForm.unit_price === '' || spForm.unit_price == null) { toast.error('Prix unitaire requis'); return; }
        setSaving(true);
        try {
            const res = await supplierProductsAPI.create({
                industrial_product_id: currentIp.id,
                supplier_id: spForm.supplier_id || supplierId,
                supplier_ref: spForm.supplier_ref || '',
                supplier_name_product: spForm.supplier_name_product.trim(),
                brand: spForm.brand || '',
                unit_price: parseFloat(spForm.unit_price),
                packaging: spForm.packaging || '',
                capacity_value: spForm.capacity_value === '' ? null : parseFloat(spForm.capacity_value),
                capacity_unit: spForm.capacity_unit || '',
                order_unit: spForm.order_unit || 'Pièce',
                billing_unit: spForm.billing_unit || 'Pièce',
                packaging_qty: spForm.unknown_packaging || spForm.packaging_qty === '' || spForm.packaging_qty == null ? null : parseFloat(spForm.packaging_qty),
                commandable: !!spForm.commandable,
                sell_by_detail: !!spForm.sell_by_detail,
                variable_weight: !!spForm.variable_weight,
                unknown_packaging: !!spForm.unknown_packaging,
                min_order_qty: parseInt(spForm.min_order_qty) || 1,
            });
            setCreated(prev => [...prev, { type: 'PF', name: res.data.supplier_name_product }]);
            toast.success(`Produit fournisseur « ${res.data.supplier_name_product} » créé`);
            if (onProductCreated) { onProductCreated(res.data); return; }
            nextLine();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de création du produit fournisseur');
        } finally { setSaving(false); }
    };

    const nextLine = () => {
        const next = qIdx + 1;
        if (next >= queue.length) {
            setStep('done');
            if (onCreated) onCreated();
            return;
        }
        setQIdx(next);
        prepareLine(queue[next]);
    };

    const currentLine = queue[qIdx];
    const supplierName = suppliers.find(s => s.id === supplierId)?.name;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-assistant-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-orange-500" /> Assistant produits par photo
                        {step === 'create' && queue.length > 0 && (
                            <span className="ml-auto text-xs font-bold bg-orange-100 text-orange-700 rounded-full px-2.5 py-1">{qIdx + 1} / {queue.length}</span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        Photographiez une facture, mercuriale ou étiquette : l'assistant contrôle l'existence des produits
                        industriels et fournisseurs, puis vous guide dans la création, étape par étape.
                    </DialogDescription>
                </DialogHeader>

                {step === 'setup' && (
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Fournisseur *</Label>
                            <Select value={supplierId} onValueChange={setSupplierId}>
                                <SelectTrigger className="h-9" data-testid="assistant-supplier-select"><SelectValue placeholder="Choisir un fournisseur" /></SelectTrigger>
                                <SelectContent>
                                    {[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr')).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Input type="file" accept="application/pdf,image/*" className="flex-1"
                                onChange={(e) => {
                                    const f = e.target.files?.[0] || null;
                                    if (f && f.type.startsWith('image/')) { setPhotos(p => [...p, f]); setFile(null); e.target.value = ''; }
                                    else { setFile(f); setPhotos([]); }
                                }} data-testid="assistant-file-input" />
                            <Button type="button" variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50" onClick={() => cameraRef.current?.click()} data-testid="assistant-camera-btn">
                                <Camera className="w-4 h-4 mr-2" /> {photos.length > 0 ? 'Ajouter une page' : 'Prendre en photo'}
                            </Button>
                            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPhotos(p => [...p, f]); setFile(null); } e.target.value = ''; }} />
                            <PhoneCaptureButton
                                testId="assistant-phone-btn"
                                onPhotos={(fs) => { setPhotos(p => [...p, ...fs]); setFile(null); }}
                            />
                        </div>
                        {photos.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                                {photos.map((p, i) => (
                                    <div key={i} className="relative border rounded overflow-hidden">
                                        <img src={URL.createObjectURL(p)} alt={`p${i}`} className="h-16 w-14 object-cover" />
                                        <button type="button" onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 text-white p-0.5"><X className="w-3 h-3" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex justify-end">
                            <Button onClick={analyze} disabled={!supplierId || (!file && photos.length === 0)} className="btn-primary" data-testid="assistant-analyze-btn">
                                <Sparkles className="w-4 h-4 mr-2" /> Analyser
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'analyzing' && (
                    <div className="flex flex-col items-center gap-3 py-10">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                        <div className="text-sm text-slate-500">Lecture du document et contrôle d'existence…</div>
                    </div>
                )}

                {step === 'review' && (
                    <div className="space-y-3 min-w-0">
                        <p className="text-xs text-slate-500">
                            {lines.length} produit(s) détecté(s) chez <strong>{supplierName}</strong>. Cochez ceux à créer, corrigez les désignations si besoin.
                            <span className="block text-emerald-700 mt-0.5">Astuce : une ligne « Existe déjà » peut aussi être cochée pour <strong>forcer la création d'un nouveau produit</strong> (PI et PF) si la reconnaissance est erronée.</span>
                        </p>
                        <div className="border rounded-lg divide-y max-h-[45vh] overflow-y-auto" data-testid="assistant-lines">
                            {lines.map((l, i) => {
                                const st = statusOf(l);
                                return (
                                    <div key={i} className={`px-3 py-2 flex items-start gap-2 ${st === 'ok' ? 'bg-emerald-50/40' : ''}`} data-testid={`assistant-line-${i}`}>
                                        <Checkbox
                                            className="mt-1"
                                            checked={selected.has(i)}
                                            onCheckedChange={() => setSelected(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                                            data-testid={`assistant-check-${i}`}
                                        />
                                        <div className="flex-1 min-w-0 space-y-1">
                                            <textarea
                                                value={l.designation}
                                                onChange={(e) => {
                                                    setLines(prev => prev.map((x, j) => j === i ? { ...x, designation: e.target.value } : x));
                                                    e.target.style.height = 'auto';
                                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                                }}
                                                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }}
                                                rows={1}
                                                className="w-full text-sm font-medium border border-input rounded-md px-2 py-1 bg-background resize-none overflow-hidden leading-snug focus:outline-none focus:ring-1 focus:ring-ring"
                                                data-testid={`assistant-designation-${i}`}
                                            />
                                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
                                                {l.ref && <span>Réf : {l.ref}</span>}
                                                {l.unit_price != null && <span>{l.unit_price} € HT</span>}
                                                {l.packaging && <span>{l.packaging}</span>}
                                                {l.ip_match && !l.sp_match && <span className="text-slate-500">PI reconnu : {l.ip_match.name}</span>}
                                                {l.sp_match && <span className="text-emerald-600">PF reconnu : {l.sp_match.name}</span>}
                                            </div>
                                        </div>
                                        <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                                            st === 'ok' ? 'bg-emerald-100 text-emerald-700' : st === 'sp_missing' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                            {st === 'ok' ? <><CheckCircle2 className="w-3 h-3" /> Existe déjà</>
                                                : st === 'sp_missing' ? <><AlertTriangle className="w-3 h-3" /> PF à créer</>
                                                    : <><XCircle className="w-3 h-3" /> PI + PF à créer</>}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setStep('setup')}>Retour</Button>
                            <Button onClick={startCreation} disabled={selected.size === 0} className="btn-primary" data-testid="assistant-start-creation-btn">
                                Créer la sélection ({selected.size}) <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        </DialogFooter>
                    </div>
                )}

                {step === 'create' && currentLine && (
                    <div className="space-y-3">
                        <div className="text-sm bg-slate-50 border rounded-lg px-3 py-2">
                            <span className="text-slate-500 text-xs">Produit en cours :</span>{' '}
                            <span className="font-semibold">{currentLine.designation}</span>
                        </div>
                        {currentLine.sp_match && (
                            <div className="text-[11px] font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md px-3 py-1.5" data-testid="assistant-forced-note">
                                Un produit fournisseur similaire existe déjà (« {currentLine.sp_match.name} ») — vous créez volontairement un nouveau produit.
                            </div>
                        )}
                        {phase === 'pi_confirm' ? (
                            <div className="space-y-3" data-testid="assistant-pi-confirm">
                                {currentIp ? (
                                    <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg px-3 py-2.5 space-y-2">
                                        <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Produit industriel reconnu :
                                        </p>
                                        <p className="text-sm font-bold text-slate-800">{currentIp.name}</p>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <Button size="sm" onClick={() => acceptIp(currentIp)} className="btn-primary" data-testid="assistant-accept-ip-btn">
                                                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accepter ce produit industriel
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={goCreateIp} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="assistant-force-new-ip-btn">
                                                <XCircle className="w-4 h-4 mr-1.5" /> Incorrect — créer un nouveau PI
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="border border-red-200 bg-red-50/60 rounded-lg px-3 py-2.5 space-y-2">
                                        <p className="text-xs font-semibold text-red-700 flex items-center gap-1">
                                            <XCircle className="w-3.5 h-3.5" /> Aucun produit industriel reconnu.
                                        </p>
                                        <Button size="sm" onClick={goCreateIp} className="btn-primary w-full sm:w-auto" data-testid="assistant-confirm-new-ip-btn">
                                            <ArrowRight className="w-4 h-4 mr-1.5" /> Confirmer : créer un nouveau PI
                                        </Button>
                                    </div>
                                )}
                                <div className="border rounded-lg px-3 py-2.5 space-y-2">
                                    {!showIpPicker ? (
                                        <Button size="sm" variant="outline" onClick={() => setShowIpPicker(true)} className="w-full sm:w-auto" data-testid="assistant-pick-ip-btn">
                                            Sélectionner moi-même un PI existant
                                        </Button>
                                    ) : (
                                        <>
                                            <Input autoFocus value={ipSearch} onChange={(e) => setIpSearch(e.target.value)} placeholder="Rechercher un produit industriel…" className="h-8 text-sm" data-testid="assistant-ip-search" />
                                            <div className="max-h-40 overflow-y-auto divide-y border rounded" data-testid="assistant-ip-results">
                                                {ips.filter(p => !ipSearch.trim() || (p.name || '').toLowerCase().includes(ipSearch.trim().toLowerCase())).slice(0, 30).map(p => (
                                                    <button key={p.id} type="button" onClick={() => acceptIp(p)}
                                                        className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-orange-50 flex items-center justify-between gap-2">
                                                        <span className="truncate">{p.name}</span>
                                                        <span className="text-[10px] text-slate-400 shrink-0">{p.category || ''}</span>
                                                    </button>
                                                ))}
                                                {ips.length > 0 && ips.filter(p => !ipSearch.trim() || (p.name || '').toLowerCase().includes(ipSearch.trim().toLowerCase())).length === 0 && (
                                                    <div className="px-2.5 py-2 text-xs text-slate-400">Aucun résultat</div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" className="text-slate-500" onClick={nextLine} data-testid="assistant-skip-pi-btn">Passer cette ligne</Button>
                                </DialogFooter>
                            </div>
                        ) : phase === 'ip' ? (
                            <div className="space-y-3" data-testid="assistant-ip-form">
                                <p className="text-xs font-semibold text-red-600 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Produit industriel introuvable — création :</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="Nom du produit industriel *" value={ipForm.name} onChange={(e) => setIpForm(f => ({ ...f, name: e.target.value }))} testId="assistant-ip-name" />
                                    <NativeSelect label="Catégorie" value={ipForm.category || ''} testId="assistant-ip-category"
                                        onChange={(e) => setIpForm(f => ({ ...f, category: e.target.value }))}
                                        options={[{ value: '', label: 'Choisir…' }, ...[...categories].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' })).map(c => ({ value: c, label: c }))]} />
                                    <NativeSelect label="Famille" value={ipForm.famille || ''} testId="assistant-ip-famille"
                                        onChange={(e) => setIpForm(f => ({ ...f, famille: e.target.value }))}
                                        options={[{ value: '', label: '—' }, ...[...familles].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' })).map(fa => ({ value: fa, label: fa }))]} />
                                    <NativeSelect label="Unité" value={ipForm.unit || 'piece'} testId="assistant-ip-unit"
                                        onChange={(e) => setIpForm(f => ({ ...f, unit: e.target.value }))}
                                        options={[{ value: 'piece', label: 'pièce' }, ...withCurrent(getBillingUnits(), ipForm.unit).filter(u => u !== 'Pièce' && u !== 'piece').map(u => ({ value: u, label: u }))]} />
                                    <Field label="TVA (%)" value={ipForm.vat_rate} type="number" onChange={(e) => setIpForm(f => ({ ...f, vat_rate: e.target.value }))} testId="assistant-ip-vat" />
                                    <div className="space-y-1 sm:col-span-2">
                                        <Label className="text-xs">Description</Label>
                                        <textarea value={ipForm.description || ''} onChange={(e) => setIpForm(f => ({ ...f, description: e.target.value }))}
                                            rows={2} className="w-full text-sm border border-input rounded-md px-2 py-1 bg-background resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                                            data-testid="assistant-ip-description" />
                                    </div>
                                    <div className="space-y-1.5 sm:col-span-2">
                                        <Label className="text-xs">Utilisable par</Label>
                                        <div className="grid grid-cols-2 gap-1.5">
                                            <CheckRow label="Fuji Express" checked={ipForm.fuji_express} onChange={(e) => setIpForm(f => ({ ...f, fuji_express: e.target.checked }))} testId="assistant-ip-fuji" />
                                            <CheckRow label="Concept Lab" checked={ipForm.concept_lab} onChange={(e) => setIpForm(f => ({ ...f, concept_lab: e.target.checked }))} testId="assistant-ip-conceptlab" />
                                            <CheckRow label="My Food" checked={ipForm.my_food} onChange={(e) => setIpForm(f => ({ ...f, my_food: e.target.checked }))} testId="assistant-ip-myfood" />
                                            <CheckRow label="Boucherie" checked={ipForm.boucherie} onChange={(e) => setIpForm(f => ({ ...f, boucherie: e.target.checked }))} testId="assistant-ip-boucherie" />
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" className="text-slate-500" onClick={nextLine} data-testid="assistant-skip-btn">Passer cette ligne</Button>
                                    <Button onClick={createIp} disabled={saving} className="btn-primary" data-testid="assistant-create-ip-btn">
                                        {saving ? 'Création…' : 'Créer le produit industriel'} <ArrowRight className="w-4 h-4 ml-1" />
                                    </Button>
                                </DialogFooter>
                            </div>
                        ) : (
                            <div className="space-y-3" data-testid="assistant-sp-form">
                                <p className="text-xs font-semibold text-amber-600 flex items-center gap-1">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Création du produit fournisseur dans « {currentIp?.name} » :
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <NativeSelect label="Fournisseur" value={spForm.supplier_id || supplierId} testId="assistant-sp-supplier"
                                        onChange={(e) => setSpForm(f => ({ ...f, supplier_id: e.target.value }))}
                                        options={[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr')).map(s => ({ value: s.id, label: s.name }))} />
                                    <div className="flex items-end pb-1.5">
                                        <CheckRow label="Commandable" checked={spForm.commandable} onChange={(e) => setSpForm(f => ({ ...f, commandable: e.target.checked }))} testId="assistant-sp-commandable" />
                                    </div>
                                    <Field label="Référence fournisseur" value={spForm.supplier_ref} onChange={(e) => setSpForm(f => ({ ...f, supplier_ref: e.target.value }))} testId="assistant-sp-ref" />
                                    <Field label="Désignation fournisseur *" value={spForm.supplier_name_product} onChange={(e) => setSpForm(f => ({ ...f, supplier_name_product: e.target.value }))} testId="assistant-sp-name" />
                                    <Field label="Marque" value={spForm.brand} onChange={(e) => setSpForm(f => ({ ...f, brand: e.target.value }))} testId="assistant-sp-brand" />
                                    <div className="space-y-1.5 sm:col-span-2 border rounded-lg px-3 py-2.5 bg-slate-50/60">
                                        <Label className="text-xs font-semibold">Unités de commande & facturation</Label>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                                            <CheckRow label="Vente au détail" checked={spForm.sell_by_detail} onChange={(e) => setSpForm(f => ({ ...f, sell_by_detail: e.target.checked }))} testId="assistant-sp-detail" />
                                            <CheckRow label="Poids variable" checked={spForm.variable_weight} onChange={(e) => setSpForm(f => ({ ...f, variable_weight: e.target.checked }))} testId="assistant-sp-variable-weight" />
                                            <CheckRow label="Colisage inconnu" checked={spForm.unknown_packaging} onChange={(e) => setSpForm(f => ({ ...f, unknown_packaging: e.target.checked }))} testId="assistant-sp-unknown-packaging" />
                                        </div>
                                        {spForm.unknown_packaging ? (
                                            <NativeSelect label="Unité de facturation" value={spForm.billing_unit} testId="assistant-sp-billing-unit-only"
                                                onChange={(e) => setSpForm(f => ({ ...f, billing_unit: e.target.value }))}
                                                options={withCurrent(getBillingUnits(), spForm.billing_unit)} />
                                        ) : (
                                            <ColisageBlock spForm={spForm} setSpForm={setSpForm} />
                                        )}
                                    </div>
                                    <Field label="Prix unitaire HT (€) *" type="number" value={spForm.unit_price} onChange={(e) => setSpForm(f => ({ ...f, unit_price: e.target.value }))} testId="assistant-sp-price" />
                                    <Field label="Qté minimum de commande" type="number" value={spForm.min_order_qty} onChange={(e) => setSpForm(f => ({ ...f, min_order_qty: e.target.value }))} testId="assistant-sp-min-qty" />
                                    <div className="grid grid-cols-2 gap-2 sm:col-span-2">
                                        <Field label="Contenance" type="number" value={spForm.capacity_value} onChange={(e) => setSpForm(f => ({ ...f, capacity_value: e.target.value }))} testId="assistant-sp-capacity" />
                                        <NativeSelect label="Unité contenance" value={spForm.capacity_unit || ''} testId="assistant-sp-capacity-unit"
                                            onChange={(e) => setSpForm(f => ({ ...f, capacity_unit: e.target.value }))}
                                            options={[{ value: '', label: '—' }, 'kg', 'g', 'L', 'cL', 'mL', 'Pièce']} />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" className="text-slate-500" onClick={nextLine} data-testid="assistant-skip-sp-btn">Passer cette ligne</Button>
                                    <Button onClick={createSp} disabled={saving} className="btn-primary" data-testid="assistant-create-sp-btn">
                                        {saving ? 'Création…' : 'Créer le produit fournisseur'} <ArrowRight className="w-4 h-4 ml-1" />
                                    </Button>
                                </DialogFooter>
                            </div>
                        )}
                    </div>
                )}

                {step === 'done' && (
                    <div className="space-y-3 text-center py-4" data-testid="assistant-done">
                        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                        <p className="font-semibold text-slate-700">{created.length} produit(s) créé(s)</p>
                        <div className="text-xs text-slate-500 space-y-0.5 max-h-40 overflow-y-auto">
                            {created.map((c, i) => <div key={i}><span className="font-bold">{c.type}</span> — {c.name}</div>)}
                        </div>
                        <Button onClick={() => { reset(); onClose(); }} className="btn-primary">Fermer</Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
